import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Card, Col, DatePicker, Empty, Input, Row, Select, Space,
  Statistic, Table, Tag, Typography, message
} from 'antd';
import {
  ReloadOutlined, PrinterOutlined, DownloadOutlined, EyeOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { ErpMasterShell } from '../../../../components/common/erp-grid';
import { accountReportsApi } from './accountReportsApi';
import {
  VOUCHER_REGISTER_STATUSES, VOUCHER_STATUS_COLOR,
  VOUCHER_TYPE_LABEL, VOUCHER_TYPES, formatINR, shortDate
} from './accountReportsConstants';
// Stage 6B.9F — friendly error / empty / retry helpers.
import { Alert } from 'antd';
import {
  friendlyAccountsErrorMessage, friendlyAccountsEmptyMessage
} from '../accountsPageState';
import '../accounts.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Voucher Register / Day Book (Stage 6B.5).
//
// Chronological list of voucher headers (not lines). Defaults to
// posted-only; pass status='all' (or pick draft/cancelled) to see
// other states. Soft-deleted vouchers are NEVER returned.
//
// Double-click a row → navigates to /erp/accounts/vouchers (the
// Journal Voucher page) where the existing modal can render the
// full voucher in read-only mode. We do NOT open an editor from
// this report — voucher mutation lives only on the Journal Voucher
// page so all Approval-MFA gating stays in one place.
// ─────────────────────────────────────────────────────────────────

function csvSafe(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function downloadCsv(filename, rows, columns) {
  const header = columns.map((c) => csvSafe(c.title)).join(',');
  const body = rows.map((r) =>
    columns.map((c) => csvSafe(typeof c.value === 'function' ? c.value(r) : r[c.dataIndex])).join(',')
  ).join('\n');
  const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function VoucherRegisterPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Stage 6B.9F — preserve last error so the page shows a Retry banner.
  const [loadError, setLoadError] = useState(null);
  const [status, setStatus] = useState('posted');
  const [voucherType, setVoucherType] = useState(null);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const navigate = useNavigate();

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const out = await accountReportsApi.voucherRegister({
        status, voucherType,
        fromDate: dateRange?.[0]?.format('YYYY-MM-DD') || null,
        toDate:   dateRange?.[1]?.format('YYYY-MM-DD') || null,
        search:   search ? search.trim() : null
      });
      setData(out);
    } catch (e) {
      setLoadError(e);
      message.error(friendlyAccountsErrorMessage(e, 'voucher_register'));
    } finally { setLoading(false); }
  }, [status, voucherType, dateRange, search]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const rows = data?.rows || [];
  const summary = data?.summary || {
    voucherCount: 0, postedCount: 0, cancelledCount: 0, draftCount: 0,
    totalDebit: 0, totalCredit: 0
  };

  const columns = useMemo(() => ([
    { title: 'Date', dataIndex: 'voucherDate', key: 'voucherDate', width: 110,
      render: shortDate },
    { title: 'Voucher No', dataIndex: 'voucherNo', key: 'voucherNo', width: 160,
      render: (v) => <code style={{ fontSize: 12 }}>{v}</code> },
    { title: 'Type', dataIndex: 'voucherType', key: 'voucherType', width: 110,
      render: (v) => <Tag color="blue">{VOUCHER_TYPE_LABEL[v] || v}</Tag> },
    { title: 'Narration', dataIndex: 'narration', key: 'narration', ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 110, align: 'center',
      render: (v) => (
        <Tag color={VOUCHER_STATUS_COLOR[v] || 'default'} style={{ marginInlineEnd: 0 }}>
          {(v || '').toUpperCase()}
        </Tag>
      ) },
    { title: 'Total Debit (₹)', dataIndex: 'totalDebit', key: 'totalDebit', width: 130, align: 'right',
      render: (v) => v > 0 ? formatINR(v) : <Text type="secondary">—</Text> },
    { title: 'Total Credit (₹)', dataIndex: 'totalCredit', key: 'totalCredit', width: 130, align: 'right',
      render: (v) => v > 0 ? formatINR(v) : <Text type="secondary">—</Text> },
    { title: 'Posted At', dataIndex: 'postedAt', key: 'postedAt', width: 150,
      render: (v) => v ? String(v).slice(0, 16).replace('T', ' ') : <Text type="secondary">—</Text> },
    { title: '', key: '__open__', width: 60, align: 'center',
      render: (_v, r) => (
        <Button type="text" size="small" icon={<EyeOutlined />}
          onClick={(e) => { e.stopPropagation(); navigate('/erp/accounts/vouchers'); }}
          title="Open in Journal Vouchers" />
      ) }
  ]), [navigate]);

  const csvColumns = useMemo(() => ([
    { title: 'Date',         value: (r) => shortDate(r.voucherDate) },
    { title: 'Voucher No',   dataIndex: 'voucherNo' },
    { title: 'Type',         dataIndex: 'voucherType' },
    { title: 'Narration',    dataIndex: 'narration' },
    { title: 'Status',       dataIndex: 'status' },
    { title: 'Total Debit',  dataIndex: 'totalDebit' },
    { title: 'Total Credit', dataIndex: 'totalCredit' },
    { title: 'Posted At',    dataIndex: 'postedAt' }
  ]), []);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <Title level={4} style={{ margin: 0 }}>Voucher Register · Day Book</Title>
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchReport} loading={loading}>Refresh</Button>
          <Button size="small" icon={<PrinterOutlined />} onClick={() => window.print()}>Print</Button>
          <Button size="small" icon={<DownloadOutlined />} disabled={!rows.length}
            onClick={() => downloadCsv('voucher-register.csv', rows, csvColumns)}>
            Export CSV
          </Button>
        </Space>
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
        Chronological list of voucher headers (Day Book). Filter by status, type, date or narration.
      </div>

      {loadError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 10 }}
          message="Could not load Voucher Register"
          description={friendlyAccountsErrorMessage(loadError, 'voucher_register')}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={fetchReport}>
              Retry
            </Button>
          }
        />
      ) : null}

      <Card size="small" className="erp-accounts-filter" style={{ marginBottom: 10 }}>
        <Space wrap>
          <Space>
            <Text>Status:</Text>
            <Select size="small" value={status} onChange={setStatus} style={{ width: 160 }}
              options={VOUCHER_REGISTER_STATUSES.map((s) => ({ value: s.value, label: s.label }))} />
          </Space>
          <Space>
            <Text>Type:</Text>
            <Select size="small" allowClear placeholder="All types" style={{ width: 160 }}
              value={voucherType} onChange={(v) => setVoucherType(v || null)}
              options={VOUCHER_TYPES.map((t) => ({ value: t, label: VOUCHER_TYPE_LABEL[t] || t }))} />
          </Space>
          <Space>
            <Text>Date:</Text>
            <RangePicker size="small" value={dateRange} onChange={setDateRange} allowClear />
          </Space>
          <Space>
            <Text>Search:</Text>
            <Input.Search
              size="small"
              placeholder="Voucher no / narration"
              allowClear value={search}
              onChange={(e) => setSearch(e.target.value)}
              onSearch={() => fetchReport()}
              style={{ width: 240 }}
            />
          </Space>
        </Space>
      </Card>

      <Row gutter={[10, 10]} style={{ marginBottom: 10 }}>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title="Vouchers" value={summary.voucherCount} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title="Posted" value={summary.postedCount}
            valueStyle={{ color: '#3f8600' }} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title="Total Debit (₹)" value={summary.totalDebit} precision={2} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title="Total Credit (₹)" value={summary.totalCredit} precision={2} /></Card>
        </Col>
      </Row>

      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 50, size: 'small', showSizeChanger: true, pageSizeOptions: ['25', '50', '100', '200'] }}
        locale={{ emptyText:
          <Empty description={friendlyAccountsEmptyMessage('voucher_register')} /> }}
        onRow={(record) => ({
          onDoubleClick: () => navigate('/erp/accounts/vouchers'),
          style: { cursor: 'pointer' }
        })}
      />
      </div>
    </ErpMasterShell>
  );
}
